import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/http/api-error';

import { useMutarOperadores } from '../configuracion.api';
import type { Operador } from '../configuracion.types';

interface OperadorDialogoProps {
  /** Operador a editar; `null` para dar de alta uno nuevo. */
  operador: Operador | null;
  onClose: () => void;
}

const MAX_NOMBRE = 100;
const MAX_CODIGO = 50;
const MAX_DESCRIPCION = 1000;

/** Mensaje de error legible para las mutaciones. */
function mensajeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return 'No se pudo guardar el operador.';
}

/**
 * Alta y edición de un operador de pago.
 *
 * Mismas validaciones que el panel anterior: nombre y código obligatorios, el
 * código se guarda en mayúsculas (la API lo normaliza y valida que sea único) y
 * la comisión debe estar entre 0 y 100 %.
 */
export function OperadorDialogo({ operador, onClose }: OperadorDialogoProps) {
  const mutaciones = useMutarOperadores();

  const [nombre, setNombre] = useState(operador?.nombre ?? '');
  const [codigo, setCodigo] = useState(operador?.codigo ?? '');
  const [comision, setComision] = useState(operador ? String(operador.comision) : '');
  const [descripcion, setDescripcion] = useState(operador?.descripcion ?? '');
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const guardando = mutaciones.crear.isPending || mutaciones.actualizar.isPending;

  const enviar = () => {
    const encontrados: Record<string, string> = {};
    const valorComision = Number(comision);

    if (!nombre.trim()) encontrados.nombre = 'El nombre es obligatorio.';
    else if (nombre.trim().length > MAX_NOMBRE) {
      encontrados.nombre = `Máximo ${MAX_NOMBRE} caracteres.`;
    }

    if (!codigo.trim()) encontrados.codigo = 'El código es obligatorio.';
    else if (codigo.trim().length > MAX_CODIGO) {
      encontrados.codigo = `Máximo ${MAX_CODIGO} caracteres.`;
    }

    if (!Number.isFinite(valorComision) || comision.trim() === '') {
      encontrados.comision = 'Indica el porcentaje de comisión.';
    } else if (valorComision <= 0 || valorComision >= 100) {
      encontrados.comision = 'Debe ser mayor que 0 y menor que 100.';
    }

    if (descripcion.trim().length > MAX_DESCRIPCION) {
      encontrados.descripcion = `Máximo ${MAX_DESCRIPCION} caracteres.`;
    }

    setErrores(encontrados);
    setAviso(null);

    if (Object.keys(encontrados).length > 0) return;

    const payload = {
      nombre: nombre.trim(),
      codigo: codigo.trim().toUpperCase(),
      comision: Number(valorComision.toFixed(2)),
      descripcion: descripcion.trim(),
    };

    const alFallar = (error: unknown) => setAviso(mensajeError(error));

    if (operador) {
      mutaciones.actualizar.mutate(
        { id: operador.id, payload },
        { onSuccess: onClose, onError: alFallar },
      );
    } else {
      mutaciones.crear.mutate(payload, { onSuccess: onClose, onError: alFallar });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={operador ? `Editar ${operador.nombre}` : 'Nuevo operador'}
      description="Operadores de pago con tarjeta y su porcentaje de comisión."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={enviar} loading={guardando}>
            {operador ? 'Guardar cambios' : 'Crear operador'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {aviso ? <Alert variant="error" title="No se pudo guardar">{aviso}</Alert> : null}

        <Input
          label="Nombre del operador"
          value={nombre}
          maxLength={MAX_NOMBRE}
          error={errores.nombre}
          onChange={(event) => setNombre(event.target.value)}
          placeholder="Ej. IZIPAY"
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Código"
            value={codigo}
            maxLength={MAX_CODIGO}
            error={errores.codigo}
            hint="Se guarda en mayúsculas y no puede repetirse."
            onChange={(event) => setCodigo(event.target.value.toUpperCase())}
            placeholder="Ej. IZI"
          />

          <Input
            label="Comisión (%)"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={comision}
            error={errores.comision}
            onChange={(event) => setComision(event.target.value)}
            placeholder="Ej. 3.50"
          />
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink-700">Descripción</span>
          <textarea
            value={descripcion}
            maxLength={MAX_DESCRIPCION}
            rows={3}
            onChange={(event) => setDescripcion(event.target.value)}
            placeholder="Notas internas sobre el operador (opcional)."
            className="w-full rounded-control border border-ink-200 bg-white px-3 py-2 text-sm text-ink-800 transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
          />
          {errores.descripcion ? (
            <span className="text-xs text-rose-600">{errores.descripcion}</span>
          ) : null}
        </label>
      </div>
    </Dialog>
  );
}
